import type { CatalogOf } from '../types';
import type { en } from './en';

export const id: CatalogOf<typeof en> = {
  common: {
    save: 'Simpan',
    cancel: 'Batal',
    apply: 'Terapkan',
    close: 'Tutup',
    back: 'Kembali',
    verify: 'Verifikasi',
    loading: 'Memuat...',
    retry: 'Coba lagi',
    or: 'atau',
    today: 'Hari ini',
    doctor: 'Dokter',
    clinic: 'Klinik',
    unknown: 'Tidak diketahui',
    copy: 'Salin',
    copied: 'Tersalin!',
    add: 'Tambah',
  },
  language: {
    label: 'Bahasa antarmuka',
    english: 'English',
    indonesian: 'Bahasa Indonesia',
  },
  header: {
    sessions: 'Sesi',
    back: 'Kembali ke sesi',
    logout: 'Keluar',
    history: 'Riwayat',
    chatWithEhr: 'Chat dengan EHR',
    dashboard: 'Dasbor',
    clinic: 'Klinik',
    aiUsage: 'Penggunaan AI',
    profile: 'Profil',
    endSession: 'Akhiri Sesi',
    stopViewing: 'Berhenti melihat',
    actingAs: 'Bertindak sebagai {name} · {clinic}',
    impersonation: '(Peniruan)',
    viewingClinic: 'Melihat: {clinic}',
    readOnly: '(Hanya baca)',
    logoAlt: 'Logo AIGP Health',
    pages: {
      consultTitle: 'Konsultasi Pasien',
      consultSubtitle: 'Skrib AI dan dokumentasi klinis',
      dashboardTitle: 'Dasbor Dokter',
      dashboardSubtitle: 'Selamat datang kembali, {name}',
      sessionsTitle: 'Kelola Sesi',
      sessionsSubtitle: 'Buat dan kelola sesi konsultasi pasien',
      meetingTitle: 'Konsultasi Daring',
      meetingSubtitle: 'Ruang pertemuan virtual untuk konsultasi pasien',
      profileTitle: 'Profil Saya',
      profileSubtitle: 'Kelola informasi akun dan preferensi Anda',
      clinicProfileTitle: 'Profil Klinik',
      clinicProfileSubtitle: 'Kelola informasi dan pengaturan klinik Anda',
      clinicAdminTitle: 'Administrasi Klinik',
      clinicAdminSubtitle: 'Selamat datang, {name}',
      adminTitle: 'Dasbor Admin',
      adminSubtitle: 'Selamat datang kembali, {name}',
      superAdminTitle: 'Dasbor Super Admin',
      superAdminSubtitle: 'Selamat datang, {name}',
      aiUsageTitle: 'Penggunaan AI',
      aiUsageSubtitle: 'Konsumsi token, biaya, dan latensi di seluruh alur AI',
      defaultTitle: 'AIGP Health',
      defaultSubtitle: 'Platform Konsultasi Medis',
      adminFallback: 'Admin',
      superAdminFallback: 'Super Admin',
    },
  },
  auth: {
    welcomeBack: 'Selamat datang kembali',
    loginSubtitle: 'Masuk untuk melanjutkan ke ruang kerja AIGP Health Anda.',
    signInEyebrow: 'Masuk',
    signInTitle: 'Masuk ke akun Anda',
    email: 'Alamat email',
    emailPlaceholder: 'anda@klinik.com',
    password: 'Kata sandi',
    passwordPlaceholder: 'Masukkan kata sandi Anda',
    showPassword: 'Tampilkan kata sandi',
    hidePassword: 'Sembunyikan kata sandi',
    signIn: 'Masuk',
    signInGoogle: 'Masuk dengan Google',
    forgotPassword: 'Lupa kata sandi?',
    createAccount: 'Buat akun',
    verifyEyebrow: 'Verifikasi identitas',
    enterCode: 'Masukkan kode Anda',
    otpHint: 'Masukkan kode 6 digit yang dikirim ke telepon terdaftar Anda melalui WhatsApp.',
    loginOtpAria: 'Kode verifikasi masuk',
    resendIn: 'Kirim ulang kode dalam {seconds}d',
    requestNewCode: 'Minta kode baru',
    secureEyebrow: 'Amankan akun Anda',
    mfaTitle: 'Siapkan autentikasi dua faktor',
    mfaHint: 'Tambahkan nomor telepon untuk menerima kode sekali pakai melalui WhatsApp.',
    phone: 'Nomor telepon',
    phonePlaceholder: 'Masukkan nomor telepon',
    sendCode: 'Kirim kode',
    resendCode: 'Kirim ulang kode',
    mfaOtpAria: 'Kode verifikasi pengaturan MFA',
    invalidEmail: 'Masukkan alamat email yang valid.',
    enterPassword: 'Masukkan kata sandi Anda.',
    emailUnverified: 'Email Anda belum diverifikasi. Verifikasi terlebih dahulu sebelum mengatur MFA.',
    authError: 'Kesalahan autentikasi. Hubungi dukungan.',
    badCredentials: 'Email atau kata sandi salah. Silakan coba lagi.',
    unableToSignIn: 'Tidak dapat masuk. Silakan coba lagi.',
    enterFullCode: 'Masukkan kode 6 digit lengkap.',
    sessionExpired: 'Sesi berakhir. Silakan masuk lagi.',
    invalidCode: 'Kode tidak valid. Silakan coba lagi.',
    couldNotResend: 'Tidak dapat mengirim ulang kode.',
    startOver: 'Mulai lagi',
    attemptRemaining: '{count} percobaan tersisa',
    attemptsRemaining: '{count} percobaan tersisa',
    invalidPhone: 'Masukkan nomor telepon yang valid.',
    couldNotSend: 'Tidak dapat mengirim kode.',
    googleUnexpected: 'Respons tak terduga dari masuk Google.',
    googleFailed: 'Masuk dengan Google gagal.',
    routingFailed: 'Masuk berhasil tetapi pengarahan gagal. Silakan coba lagi.',
    shellDefaultTitle: 'Selamat datang di AIGP Health',
    shellDefaultSubtitle: 'Dukungan keputusan klinis berbasis bukti, untuk reumatologi dan praktik umum.',
    trustHipaa: 'Penanganan data selaras HIPAA',
    trustEncrypted: 'Terenkripsi saat dikirim dan disimpan',
    trustEvidence: 'Bukti yang diverifikasi klinisi',
    registrationPhases: 'Tahap pendaftaran',
    trustAria: 'Kepercayaan dan keamanan',
    progress: 'Progres',
    disclaimerTitle: 'Penafian',
    disclaimerBody:
      'AIGP Health dan platform AI-nya "Anzu" dimaksudkan untuk membantu — bukan menggantikan — penilaian klinis tenaga kesehatan yang kompeten. Semua keputusan klinis harus dibuat oleh dokter berizin. Informasi dari Anzu bukan pengganti nasihat, diagnosis, atau pengobatan medis profesional.',
    iUnderstand: 'Saya mengerti',
  },
  forgot: {
    title: 'Atur ulang kata sandi',
    subtitle: 'Kami akan membantu Anda kembali ke akun Anda.',
    eyebrow: 'Pemulihan akun',
    stepEmail: 'Atur ulang kata sandi',
    stepOtp: 'Verifikasi kode',
    stepReset: 'Atur kata sandi baru',
    hintEmail: 'Masukkan email Anda dan kami akan mengirim kode pengaturan ulang.',
    hintOtp: 'Masukkan kode 6 digit yang dikirim ke telepon Anda.',
    hintReset: 'Masukkan token dari email Anda dan pilih kata sandi baru.',
    sendResetCode: 'Kirim kode pengaturan ulang',
    otpAria: 'Kode pengaturan ulang kata sandi',
    resetToken: 'Token pengaturan ulang',
    tokenPlaceholder: 'Tempel token dari email Anda',
    newPassword: 'Kata sandi baru',
    newPasswordPlaceholder: 'Masukkan kata sandi baru',
    confirmPassword: 'Konfirmasi kata sandi baru',
    confirmPlaceholder: 'Masukkan ulang kata sandi baru Anda',
    resetPassword: 'Atur ulang kata sandi',
    backToSignIn: 'Kembali ke masuk',
    codeSent: 'Kode pengaturan ulang telah dikirim ke telepon Anda.',
    codeResent: 'Kode pengaturan ulang baru telah dikirim.',
    sendFailed: 'Gagal mengirim kode. Silakan coba lagi.',
    resendFailed: 'Gagal mengirim ulang kode.',
    enterFullCode: 'Masukkan kode 6 digit lengkap.',
    tokenSent: 'Token pengaturan ulang dikirim ke email Anda. Masukkan di bawah bersama kata sandi baru.',
    invalidCode: 'Kode tidak valid. Silakan coba lagi.',
    enterToken: 'Masukkan token pengaturan ulang dari email Anda.',
    passwordRequirements: 'Kata sandi tidak memenuhi persyaratan.',
    passwordsMismatch: 'Kata sandi tidak cocok.',
    resetSuccess: 'Kata sandi berhasil diatur ulang. Mengarahkan ke masuk…',
    resetFailed: 'Gagal mengatur ulang kata sandi. Silakan coba lagi.',
  },
  dashboard: {
    approvalTitle: 'Persetujuan Klinik Tertunda',
    approvalBody: 'Pendaftaran klinik Anda sedang ditinjau oleh tim administrasi kami.',
    approvalAccess: 'Anda dapat mengakses portal dokter setelah klinik Anda disetujui.',
    approvalHelp: 'Hubungi administrator klinik atau dukungan jika Anda memiliki pertanyaan.',
    welcome: 'Selamat siang, Dr. {name}!',
    welcomeBody: 'Siap mengelola sesi pasien dan memberikan perawatan terbaik',
    whatToDo: 'Apa yang ingin Anda lakukan?',
    manageSessions: 'Kelola Sesi',
    manageSessionsBody: 'Buat, sunting, dan atur sesi konsultasi pasien',
    manageFeature1: 'Jadwalkan konsultasi baru',
    manageFeature2: 'Perbarui informasi pasien',
    manageFeature3: 'Kelola detail janji temu',
    clinicAdmin: 'Administrasi Klinik',
    clinicAdminBody: 'Kelola pengguna, langganan, dan ringkasan klinik',
    clinicFeature1: 'Ikhtisar & analitik',
    clinicFeature2: 'Manajemen pengguna',
    clinicFeature3: 'Manajemen langganan',
  },
  usage: {
    loadingChip: 'Sesi…',
    loadingBanner: 'Memuat penggunaan sesi Anda…',
    loadingTitle: 'Memuat penggunaan sesi',
    errorChip: 'Sesi —',
    errorBanner: 'Tidak dapat memuat penggunaan sesi Anda. {error}',
    loadFailed: 'Gagal memuat penggunaan sesi',
    unlimitedTitle: 'Sesi tanpa batas — tanpa kuota bulanan',
    usedTitle: '{used} dari {limit} sesi terpakai bulan ini',
    unlimitedChip: 'Sesi ∞',
    sessionsLabel: 'sesi',
    unlimitedBanner: 'Tanpa batas — tanpa kuota bulanan.',
    usedThisPeriod: 'Anda telah memakai {used} sesi pada periode ini.',
    usedThisPeriodPlural: 'Anda telah memakai {used} sesi pada periode ini.',
    reachedLimit: 'Anda telah mencapai batas sesi bulanan ({used}/{limit}). Sesi diatur ulang pada periode berikutnya.',
    oneLeft: '1 sesi tersisa bulan ini. {used}/{limit} terpakai.',
    manyLeft: '{remaining} sesi tersisa bulan ini. {used}/{limit} terpakai.',
  },
  inactivity: {
    title: 'Sesi Akan Berakhir',
    body: 'Sesi Anda akan berakhir karena tidak aktif dalam:',
    continueQuestion: 'Apakah Anda ingin melanjutkan sesi?',
    logoutNow: 'Keluar sekarang',
    continueSession: 'Lanjutkan sesi',
  },
  subscription: {
    title: 'Langganan Tertunda',
    bodyNamed: 'Dr. {name}, akun Anda sudah siap, tetapi klinik Anda belum memiliki langganan aktif.',
    bodyUnnamed: 'Akun Anda sudah siap, tetapi klinik Anda belum memiliki langganan aktif.',
    access: 'Anda dapat mengakses portal dokter segera setelah klinik Anda berlangganan.',
    help: 'Hanya administrator klinik yang dapat mengaktifkan langganan. Setelah itu, periksa lagi di bawah atau masuk kembali.',
    checking: 'Memeriksa…',
    checkAgain: 'Periksa lagi',
    stillPending: 'Klinik Anda masih belum memiliki langganan aktif. Silakan periksa dengan administrator klinik.',
    checkFailed: 'Tidak dapat memeriksa status langganan. Silakan coba lagi sebentar.',
  },
  sessions: {
    tabSessions: 'Sesi',
    tabSignature: 'Tanda tangan',
    tabMedicationCatalog: 'Katalog Obat',
    tabProtocols: 'Protokol',
    tabEducation: 'Konten Edukasi',
    tabScheduling: 'Penjadwalan',
    noSignature: 'Belum ada tanda tangan',
    filterToday: 'Hari ini',
    filterTomorrow: 'Besok',
    filterThisWeek: 'Minggu ini',
    filterAll: 'Semua',
    filterCustom: 'Rentang kustom',
    listView: 'Tampilan daftar',
    calendarView: 'Tampilan kalender',
    allDoctors: 'Semua dokter',
    sortBy: 'Urutkan:',
    sortDefault: 'Default (terbaru dulu)',
    sortDateNewest: 'Tanggal janji — terbaru dulu',
    sortDateOldest: 'Tanggal janji — terlama dulu',
    sortTimeEarliest: 'Waktu janji — paling awal',
    sortTimeLatest: 'Waktu janji — paling akhir',
    sortIdLow: 'ID sesi — rendah ke tinggi',
    sortIdHigh: 'ID sesi — tinggi ke rendah',
    searchPlaceholder: 'Cari nama atau telepon pasien...',
    bulkUpload: 'Unggah massal',
    createNew: 'Buat sesi baru',
    loading: 'Memuat sesi...',
    appointmentsHeading: 'Janji temu {filter}',
    appointmentsRange: 'Janji temu {start} - {end}',
    emptyTitle: 'Tidak ada janji temu {filter}',
    emptySearch: 'Tidak ada janji temu {filter} yang cocok dengan pencarian Anda.',
    emptyScheduled: 'Tidak ada janji temu dijadwalkan untuk {filter}.',
    emptyRange: 'Tidak ada janji temu untuk {start} - {end}',
    emptyRangeSearch: 'Tidak ada janji temu pada rentang tanggal yang dipilih yang cocok dengan pencarian Anda.',
    emptyAll: 'Belum ada sesi. Buat sesi pertama Anda untuk memulai.',
    emptyRangeScheduled: 'Tidak ada janji temu dijadwalkan untuk rentang tanggal yang dipilih.',
    emptyPickRange: 'Pilih rentang tanggal untuk melihat janji temu.',
    showingRange: 'Menampilkan {start}-{end} dari {total}',
    colPatient: 'Nama pasien',
    colPatientHint: 'Gunakan pencarian untuk menemukan pasien',
    colPhone: 'Nomor telepon',
    colDoctor: 'Dokter',
    colNotification: 'Notifikasi',
    colPreconsult: 'Pra-konsultasi',
    colType: 'Jenis sesi',
    sent: 'Terkirim',
    notSent: 'Belum terkirim',
    rowsPerPage: 'Baris per halaman:',
    statusNotArrived: 'Belum tiba',
    statusCheckedIn: 'Sudah check-in',
    statusInConsultation: 'Sedang konsultasi',
    statusCompleted: 'Sesi selesai',
    statusCompletedShort: 'Selesai',
    statusNoShow: 'Tidak hadir',
    statusCancelled: 'Dibatalkan',
    createTitle: 'Buat sesi baru',
    searchMrn: 'Cari berdasarkan MRN',
    searchMrnPlaceholder: 'Masukkan 4+ digit terakhir MRN untuk mencari',
    searching: 'Mencari...',
    patientName: 'Nama pasien *',
    patientNamePlaceholder: 'Masukkan nama lengkap pasien',
    phone: 'Nomor telepon *',
    phonePlaceholder: 'Masukkan nomor telepon',
    mrnOptional: 'Nomor rekam medis (opsional)',
    mrnPlaceholder: 'Masukkan MRN klinik jika ada',
    dob: 'Tanggal lahir *',
    dobRequired: 'Tanggal lahir wajib diisi',
    dobMissingOnRecord:
      'Data pasien ini belum memiliki tanggal lahir. Tambahkan untuk membuat sesi.',
    patientMatchHint:
      'Ketik minimal dua huruf nama untuk melihat pasien yang sudah ada.',
    matchingPatients: 'Pasien yang cocok',
    sameNameWarning:
      'Periksa: lebih dari satu catatan memiliki nama ini. Pastikan dengan digit telepon atau tanggal lahir sebelum memilih.',
    noneOfTheseNewPatient: 'Bukan salah satunya — lanjutkan sebagai pasien baru',
    sendLinkHint: 'Kirim tautan pra-konsultasi',
    resendLinkHint: 'Kirim tautan pra-konsultasi baru',
    sendLinkTitle: 'Kirim tautan pra-konsultasi?',
    resendLinkTitle: 'Kirim tautan pra-konsultasi baru?',
    resendLinkBody: 'Tautan pra-konsultasi akan dikirim melalui WhatsApp ke {name} ({phone}).',
    resendLinkSupersedes: 'Tautan yang dikirim sebelumnya akan berhenti berfungsi begitu tautan baru dikirim.',
    resendLinkWindow: 'Tautan mengikuti jadwal kunjungan: aktif dua hari sebelum janji temu dan berhenti berfungsi beberapa jam setelah dimulai.',
    resendLinkConfirm: 'Kirim tautan',
    resendLinkSent: 'Tautan pra-konsultasi dikirim ke {name}. Tautan sebelumnya tidak berlaku lagi.',
    resendLinkAlreadyDone: 'Tautan tidak dikirim: pasien ini sudah menyelesaikan pra-konsultasi untuk sesi ini.',
    resendLinkFailed: 'Tautan pra-konsultasi tidak dapat dikirim.',
    assignDoctor: 'Tetapkan dokter *',
    selectDoctor: 'Pilih dokter',
    appointmentDate: 'Tanggal janji temu *',
    appointmentTime: 'Waktu janji temu *',
    sessionType: 'Jenis sesi *',
    typePhysical: 'Tatap muka',
    typeOnline: 'Daring',
    practitionerType: 'Jenis praktisi *',
    patientLanguage: 'Bahasa pasien',
    rheumatologist: 'Reumatolog',
    dermatologist: 'Dokter kulit',
    generalPractitioner: 'Dokter Umum',
    viewTitle: 'Lihat detail sesi',
    viewConfirm: 'Lihat sesi',
    viewSubtitle: 'Anda akan melihat informasi sesi',
    sessionInformation: 'Informasi sesi',
    sessionId: 'ID sesi',
    patient: 'Pasien',
    date: 'Tanggal',
    time: 'Waktu',
    status: 'Status',
    editAppointment: 'Sunting janji temu',
    selectDateRange: 'Pilih rentang tanggal',
    from: 'Dari:',
    to: 'Sampai:',
    bulkTitle: 'Unggah janji temu massal',
    csvRequirements: 'Persyaratan file CSV:',
    csvNoHeaders: 'File harus berformat CSV (.csv) tanpa header',
    csvMaxSize: 'Ukuran file maksimum: 5MB',
    dropCsv: 'Klik untuk memilih file CSV atau seret dan lepas',
    maxSizeHint: 'Ukuran file maks: 5MB',
    bulkSummary: 'Ringkasan unggah massal',
    totalProcessed: 'Total diproses',
    successful: 'Berhasil',
    failed: 'Gagal',
    skipped: 'Dilewati',
    ignored: 'Diabaikan',
    issues: 'Masalah yang ditemukan:',
    downloadFailed: 'Unduh CSV catatan gagal',
    calToday: 'Hari ini',
    patientUnavailable: 'Pasien tidak tersedia',
    unassigned: 'Belum ditetapkan',
    notSet: 'Belum diatur',
    sessionCalendar: 'Kalender sesi',
    colAppointmentDate: 'Tanggal janji temu',
    colAppointmentTime: 'Waktu janji temu',
    clickToView: 'Klik untuk melihat detail sesi',
    editAppointmentHint: 'Sunting tanggal/waktu janji temu',
    sessionCount: '{count} sesi',
    sessionCountPlural: '{count} sesi',
    weekdayMon: 'Sen',
    weekdayTue: 'Sel',
    weekdayWed: 'Rab',
    weekdayThu: 'Kam',
    weekdayFri: 'Jum',
    weekdaySat: 'Sab',
    weekdaySun: 'Min',
    loadFailed: 'Gagal memuat sesi. Silakan coba lagi.',
    navigateFailed: 'Gagal membuka sesi. Silakan coba lagi.',
    appointmentUpdated: 'Tanggal/waktu janji temu diperbarui untuk sesi #{id}',
    updateAppointmentFailed: 'Gagal memperbarui janji temu. Silakan coba lagi.',
    dateRequired: 'Tanggal janji temu wajib diisi',
    datePast: 'Tanggal janji temu tidak boleh di masa lalu (waktu Singapura)',
    timeRequired: 'Waktu janji temu wajib diisi',
    nameRequired: 'Nama pasien wajib diisi',
    nameInvalid: 'Masukkan nama yang valid (2-50 karakter)',
    phoneRequired: 'Nomor telepon wajib diisi',
    phoneInvalid: 'Masukkan nomor telepon yang valid',
    doctorRequired: 'Pilih dokter yang akan ditetapkan',
    sessionCreated: 'Sesi berhasil dibuat! ID sesi: {id}',
    createFailed: 'Gagal membuat sesi. Silakan coba lagi.',
    statusUpdated: 'Status janji temu diperbarui menjadi "{status}"',
    statusUpdateFailed: 'Gagal memperbarui status janji temu. Silakan coba lagi.',
    maxRows: 'Maksimum {max} baris per halaman.',
    selectValidCsv: 'Pilih file CSV yang valid',
    fileTooLarge: 'Ukuran file harus kurang dari 5MB',
    selectCsv: 'Pilih file CSV',
    uploadFailed: 'Gagal mengunggah file CSV. Silakan coba lagi.',
    dropValidCsv: 'Letakkan file CSV yang valid',
    csvColumnOrder: 'Urutan kolom wajib (15 kolom):',
    downloadSample: 'Unduh contoh CSV',
    changeFile: 'Ganti file',
    uploading: 'Mengunggah...',
    uploadCsv: 'Unggah CSV',
    failedRecords: 'Catatan gagal ({count})',
    skippedRecords: 'Catatan dilewati ({count})',
    ignoredRecords: 'Catatan diabaikan ({count})',
    rowLabel: 'Baris {row}',
    unknownError: 'Kesalahan tidak diketahui',
    unknownReason: 'Alasan tidak diketahui',
    moreFailed: '...dan {count} catatan gagal lainnya',
    moreSkipped: '...dan {count} catatan dilewati lainnya',
    moreIgnored: '...dan {count} catatan diabaikan lainnya',
  },
  consult: {
    readyTitle: 'Siap mentranskripsi',
    readyBody: 'Mulai merekam untuk menangkap konsultasi',
    consultLanguage: 'Bahasa konsultasi',
    transcriptionLanguage: 'Bahasa Konsultasi',
    outputLanguage: 'Bahasa Catatan',
    outputLanguageHint: 'Catatan dan analisis AI akan ditulis dalam bahasa catatan',
    consultLanguageLocked: 'Tidak termasuk dalam paket Anda',
    startRecording: 'Mulai rekaman',
    uploadWav: 'Unggah WAV (Dev)',
    uploading: 'Mengunggah rekaman...',
    transcribing: 'Mentranskripsi audio...',
    analyzing: 'Menganalisis konsultasi...',
    complete: 'Selesai!',
    processing: 'Memproses...',
    uploadingHint: 'Mengirim audio ke server',
    transcribingHint: 'Mengubah ucapan menjadi teks',
    analyzingHint: 'Menghasilkan wawasan klinis',
    completeHint: 'Menyelesaikan',
    processingHint: 'Mohon tunggu',
    initializing: 'Menyiapkan...',
    initializingHint: 'Menyiapkan koneksi audio',
    recording: 'Merekam',
    paused: 'Dijeda',
    resume: 'Lanjutkan',
    pause: 'Jeda',
    stop: 'Berhenti',
    recordingPaused: 'Rekaman dijeda',
    recordingAudio: 'Merekam audio konsultasi...',
    stopWhenFinished: 'Klik Berhenti jika selesai',
    stopped: 'Berhenti',
    startNewRecording: 'Mulai rekaman baru',
    recordingComplete: 'Rekaman selesai',
    insertHint: 'Klik "Masukkan ke catatan" untuk mentranskripsi dan menganalisis',
    insertToNotes: 'Masukkan ke catatan',
    consentTitle: 'Persetujuan pasien diperlukan',
    consentBody:
      'Sebelum memulai sesi rekaman untuk transkripsi medis, pastikan Anda telah memperoleh persetujuan yang sesuai dari pasien.',
    consentCheckbox:
      'Saya mengonfirmasi bahwa saya telah memperoleh persetujuan yang sesuai dari pasien untuk rekaman audio dan transkripsi konsultasi medis ini.',
    acknowledge: 'Mengerti',
    startTitle: 'Mulai rekaman',
    startBody: 'Yakin ingin mulai merekam? Ini akan menangkap audio dari mikrofon Anda untuk transkripsi.',
    yesStart: 'Ya, mulai rekaman',
    privateNotes: 'Catatan pribadi',
    saving: '(Menyimpan...)',
    privateNotesPlaceholder: 'Tambahkan catatan pribadi tentang pasien ini...',
    visitsTitle: 'Linimasa semua kunjungan',
    loadingHistory: 'Memuat riwayat pasien...',
    sessionNumber: 'Sesi #{id}',
    appointmentTime: 'Waktu janji: {time}',
    noPreviousVisits: 'Tidak ada kunjungan sebelumnya untuk pasien ini.',
    languageUpdateFailed: 'Gagal memperbarui bahasa konsultasi',
    inConsultSaved: 'Data dalam konsultasi disimpan',
    inConsultSaveFailed: 'Gagal menyimpan data dalam konsultasi',
    signedOff: 'Sesi ditandatangani dan diakhiri.',
    endFailed: 'Gagal mengakhiri sesi. Silakan coba lagi.',
    noAudio: 'Tidak ada audio yang direkam',
    analysisFailed: 'Analisis gagal setelah percobaan ulang — silakan coba lagi',
    transcribed: 'Rekaman ditranskripsi dan dianalisis',
    processFailed: 'Gagal memproses rekaman',
    recovered: 'Rekaman yang dipulihkan ditranskripsi dan dianalisis',
    recoverFailed: 'Gagal memulihkan rekaman',
    wavDone: 'File WAV ditranskripsi dan dianalisis',
    wavFailed: 'Gagal memproses unggahan WAV',
    alreadySignedOff: 'Sesi sudah ditandatangani',
    alreadySignedOffBody:
      'Sesi ini sudah ditandatangani, sehingga obatnya telah disetujui. Menjalankan ulang scribe dapat merevisi atau menggandakan resep yang disetujui. Lanjutkan hanya jika Anda ingin memperbarui catatan konsultasi ini.',
    rerunScribe: 'Jalankan ulang scribe',
    endTitle: 'Akhiri sesi dan tandatangani?',
    endBody: 'Ini menyelesaikan konsultasi dan mengirim catatan ke sistem klinik.',
    endConfirm: 'Akhiri sesi',
    sectionLoading: 'Menganalisis...',
    sectionFailed: 'Analisis gagal, menampilkan catatan sebelumnya',
    analyzingProgressive: 'Menyusun catatan bagian demi bagian...',
    analyzingProgressiveHint: 'Setiap bagian tampil segera setelah siap',
    runInProgress: 'Analisis scribe masih berjalan. Harap tunggu hingga semua bagian selesai.',
    scribeAgents: {
      history: 'Riwayat',
      examination: 'Pemeriksaan',
      diagnosis: 'Diagnosis',
      medication: 'Obat-obatan',
      eq: 'Pengingat hubungan',
      followup: 'Tindak lanjut',
    },
  },
  workspace: {
    tabs: {
      preClerk: 'Pra-Klerk',
      inConsult: 'Dalam Konsultasi',
      workflow: 'Alur Kerja',
      sessionSummary: 'Ringkasan Sesi',
      chroniq: 'Chroniq',
      clinicalSummary: 'Ringkasan Klinis',
      careGaps: 'Kesenjangan perawatan',
    },
    careGaps: {
      title: 'Kesenjangan perawatan',
      subtitle: 'Target vs. aktivitas penyakit saat ini, dari kunjungan yang telah disahkan',
      ruleset: 'Aturan {version}',
      openCount: '{count} terbuka',
      onTarget: 'Sesuai target',
      reevaluate: 'Evaluasi ulang',
      reevaluating: 'Mengevaluasi…',
      loading: 'Memeriksa kesenjangan perawatan…',
      empty: 'Sesuai target. Tidak ada kesenjangan terbuka.',
      silent: 'Mesin sedang mengevaluasi secara diam-diam. Kesenjangan akan muncul di sini setelah tampilan diaktifkan untuk klinik ini.',
      showHistory: 'Tampilkan {count} yang selesai atau ditutup',
      hideHistory: 'Sembunyikan yang selesai dan ditutup',
      recorded: 'Tercatat',
      noScore: 'Tidak ada skor aktivitas yang tercatat',
      targetNotMet: 'Target belum tercapai',
      targetMet: 'Target tercapai',
      targetUnknown: 'Target: tidak dapat ditentukan',
      action: 'Tindakan yang disarankan',
      due: 'Jatuh tempo {date}',
      closedOn: 'Ditutup {date}',
      close: 'Tutup kesenjangan',
      exception: 'Tambah pengecualian',
      rule: {
        measurementMissing: 'Skor aktivitas penyakit belum tercatat',
        aboveTarget: 'Aktivitas penyakit di atas target',
      },
      disease: { ra: 'RA', psa: 'PsA', axspa: 'axSpA', sle: 'SLE' },
      status: {
        open: 'Terbuka',
        resolved: 'Selesai',
        excepted: 'Dikecualikan',
        closed: 'Ditutup',
        withdrawn: 'Ditarik',
      },
      dialog: {
        closeTitle: 'Tutup kesenjangan perawatan',
        closeHint: 'Catat bukti bahwa kriteria penutupan telah terpenuhi.',
        evidenceLabel: 'Bukti penutupan',
        evidencePlaceholder: 'mis. CDAI 8 tercatat hari ini; target tercapai',
        confirmClose: 'Tutup kesenjangan',
        exceptionTitle: 'Catat pengecualian',
        exceptionHint: 'Target individual atau alasan terdokumentasi untuk menerima aktivitas saat ini. Kesenjangan ditahan sampai pengecualian berakhir.',
        rationaleLabel: 'Alasan',
        rationalePlaceholder: 'mis. Kehamilan: treat-to-target ditunda; tinjau pada minggu ke-20',
        expiresLabel: 'Tinjau sebelum',
        expiresHint: 'Harus tanggal mendatang, paling lama 12 bulan.',
        confirmException: 'Simpan pengecualian',
        cancel: 'Batal',
        saving: 'Menyimpan…',
      },
      errors: {
        displayOff: 'Kesenjangan perawatan belum diaktifkan untuk klinik ini.',
        evidenceRequired: 'Bukti penutupan wajib diisi.',
        rationaleRequired: 'Alasan wajib diisi.',
        notFound: 'Kesenjangan perawatan ini sudah tidak terbuka.',
        invalidException: 'Pengecualian harus berakhir pada tanggal mendatang dalam 12 bulan.',
        generic: 'Tidak dapat memperbarui kesenjangan perawatan. Silakan coba lagi.',
      },
    },
    subTabs: {
      history: 'Riwayat (Hx)',
      exam: 'Pemeriksaan',
      dx: 'Dx/Asesmen',
      plan: 'Rencana / Mx',
      billing: 'Penagihan & Order',
      pro: 'PRO',
      aiScribe: 'AI Scribe',
    },
    headings: {
      practitionerNotes: 'Catatan Praktisi',
      practitionerNotesEmpty: 'Belum ada catatan praktisi. Klik untuk menambah.',
      practitionerNotesPlaceholder: 'Klik untuk menambah catatan...',
      hpi: 'Riwayat Penyakit Sekarang',
      hpiPlaceholder: 'Masukkan riwayat penyakit sekarang...',
      hpiEmpty: 'Belum ada riwayat penyakit sekarang. Klik untuk menambah.',
      ros: 'Tinjauan Sistem',
      rosPlaceholder: 'Masukkan tinjauan sistem (mis., Konstitusi: Menyangkal demam...)',
      rosEmpty: 'Belum ada tinjauan sistem. Klik untuk menambah.',
      pastMedicalHx: 'RIWAYAT MEDIS LALU',
      pastMedicalHxPlaceholder: 'Masukkan riwayat medis lalu...',
      pastMedicalHxEmpty: 'Belum ada riwayat medis lalu. Klik untuk menambah.',
      pastSurgicalHx: 'RIWAYAT BEDAH LALU',
      pastSurgicalHxPlaceholder: 'Masukkan riwayat bedah lalu...',
      pastSurgicalHxEmpty: 'Belum ada riwayat bedah lalu. Klik untuk menambah.',
      familyHistory: 'RIWAYAT KELUARGA',
      familyHistoryAdd: 'Tambah',
      familyHistoryAddTitle: 'Tambah anggota keluarga',
      familyHistoryEmpty: 'Belum ada riwayat keluarga',
      familyHistoryEmptyHint: 'Mulai dengan menambahkan pasien (proband) ke pohon keluarga.',
      familyHistoryStart: 'Mulai pohon keluarga',
      familyHistoryExpanded: 'Riwayat Keluarga - Tampilan diperluas',
      familyHistoryMale: 'Laki-laki',
      familyHistoryFemale: 'Perempuan',
      familyHistoryAffected: 'Terkena',
      familyHistoryDeceased: 'Meninggal',
      medsAllergies: 'OBAT & ALERGI',
      medications: 'Obat:',
      medicationsPlaceholder: 'Masukkan obat saat ini...',
      medicationsEmpty: 'Belum ada obat tercatat. Klik untuk menambah.',
      allergies: 'Alergi:',
      allergiesPlaceholder: 'Masukkan alergi...',
      allergiesEmpty: 'Belum ada alergi tercatat. Klik untuk menambah.',
      socialHistory: 'RIWAYAT SOSIAL',
      socialHistoryPlaceholder: 'Masukkan riwayat sosial...',
      socialHistoryEmpty: 'Belum ada riwayat sosial. Klik untuk menambah.',
      historySection: 'RIWAYAT',
      rosPlaceholderBrief: 'Masukkan tinjauan sistem...',
      hpiEmptyBrief: 'Belum ada riwayat penyakit sekarang.',
      rosEmptyBrief: 'Belum ada tinjauan sistem.',
      pastMedicalHxEmptyBrief: 'Belum ada riwayat medis lalu.',
      pastSurgicalHxEmptyBrief: 'Belum ada riwayat bedah lalu.',
      medicationsEmptyBrief: 'Belum ada obat tercatat.',
      socialHistoryEmptyBrief: 'Belum ada riwayat sosial.',
      copyToClipboard: 'Salin ke papan klip',
    },
    left: {
      mrn: 'MRN',
      noMrn: 'MRN tidak tersedia',
      noName: 'Nama tidak tersedia',
      ageGender: 'USIA / JENIS KELAMIN',
      lastVisit: 'KUNJUNGAN TERAKHIR',
      noPreviousVisits: 'Tidak ada kunjungan sebelumnya',
      privateNote: 'CATATAN PRIBADI',
      privateNotePlaceholder: 'Tambahkan konteks klinis...',
      activeProblems: 'Masalah Aktif',
      noActiveProblems: 'Tidak ada masalah aktif',
      activeMedications: 'Obat Aktif',
      noActiveMedications: 'Tidak ada obat aktif',
      lastInvestigation: 'Pemeriksaan Terakhir',
      noData: 'Tidak ada data',
      moreOptions: 'Opsi lainnya',
      edit: 'Sunting',
    },
    alerts: {
      title: 'Alergi & Peringatan',
      drugAllergies: 'Alergi obat',
      otherAlerts: 'Peringatan lain',
      noAllergyRecord: 'Belum ada catatan alergi.',
      noKnownDrugAllergies: 'Tidak ada alergi obat yang diketahui',
      addAllergy: 'Tambah alergi',
      addAlert: 'Tambah peringatan',
      allergyPlaceholder: 'Obat dan reaksi, mis. Penisilin - ruam',
      alertPlaceholder: 'Isi peringatan',
      medicalAlert: 'Peringatan medis',
      anticoagulation: 'Antikoagulasi',
      fromChat: 'Dari obrolan pra-konsultasi',
      recordedByDoctor: 'Dicatat oleh dokter',
      edit: 'Sunting',
      remove: 'Hapus',
      confirmRemove: 'Hapus entri ini?',
      loadFailed: 'Alergi dan peringatan tidak dapat dimuat.',
      saveFailed: 'Tidak dapat menyimpan: {reason}',
    },
    right: {
      timeline: 'LINIMASA',
      rx: 'RX',
      review: 'TINJAU',
      ix: 'IX',
      orders: 'ORDER',
      quickRx: 'QUICK RX',
      letters: 'SURAT',
      visitTimeline: 'Linimasa Kunjungan',
      loadingHistory: 'Memuat riwayat pasien...',
      noPreviousVisits: 'Tidak ada kunjungan sebelumnya.',
      sessionLabel: 'Sesi #{id} - {name}',
      patientFallback: 'Pasien',
      currentlyViewing: 'Sedang dilihat',
      didNotAttend: 'Tidak hadir',
      completed: 'Selesai',
      closed: 'Ditutup',
      consultationComplete: 'Konsultasi selesai',
      inProgress: 'Sedang berlangsung',
    },
    preclerk: {
      patientHistory: 'Riwayat & Gejala Pasien (Dihasilkan AI)',
      noPatientHistory: 'Riwayat pasien tidak tersedia',
      ros: 'Tinjauan Sistem:',
      noRos: 'Tinjauan sistem tidak tersedia',
      pastMedical: 'Keluhan Medis Lalu:',
      noPastMedical: 'Keluhan medis lalu tidak tersedia',
      pastSurgical: 'Keluhan Bedah Lalu:',
      noPastSurgical: 'Keluhan bedah lalu tidak tersedia',
      familyHistory: 'Riwayat Keluarga:',
      noFamilyHistory: 'Riwayat keluarga tidak tersedia',
      drugAllergies: 'Obat & Alergi:',
      noDrugAllergies: 'Obat atau alergi tidak tersedia',
      socialHistory: 'Riwayat Sosial:',
      noSocialHistory: 'Riwayat sosial tidak tersedia',
    },
    exam: {
      msk: 'MSK',
      general: 'Umum',
      skinHeent: 'Kulit/HEENT',
      eyes: 'Mata',
      cvs: 'Kardio',
      respiratory: 'Napas',
      abdominal: 'Abdomen',
      neuro: 'Saraf',
      summary: 'Ringkasan',
      skinExam: 'Pemeriksaan kulit',
      generalAppearance: 'PENAMPILAN UMUM',
      skinMucocutaneous: 'KULIT & MUKOKUTAN',
      examinerSummary: 'RINGKASAN & KESAN PEMERIKSA',
      diseaseActivity: 'SKOR AKTIVITAS PENYAKIT & PRO',
      cardiovascular: 'KARDIOVASKULAR',
      jointExam: 'MUSKULOSKELETAL / PEMERIKSAAN SENDI',
      neurological: 'NEUROLOGIS',
      eyesSection: 'MATA',
      respiratorySection: 'RESPIRASI',
      abdominalSection: 'ABDOMEN',
      lymphoreticular: 'LIMFORETIKULER & SALIVA',
    },
    dx: {
      section: 'DX / ASESMEN',
      primary: 'DIAGNOSIS UTAMA',
      principal: 'Masalah Utama',
      acute: 'Diagnosis Akut',
      chronic: 'Diagnosis Kronis',
      differential: 'Diagnosis Banding yang Dipertimbangkan',
      clinicalReasoning: 'Penalaran Klinis',
      addDiagnosis: 'Tambah Diagnosis',
      addAcute: 'Tambah Diagnosis Akut',
      addChronic: 'Tambah Diagnosis Kronis',
      addDifferential: 'Tambah Diagnosis Banding',
      noPrimary: 'Diagnosis utama tidak tersedia',
      noAcute: 'Tidak ada diagnosis akut',
      noChronic: 'Tidak ada diagnosis kronis',
      noDifferential: 'Tidak ada diagnosis banding yang dipertimbangkan',
      insights: 'Wawasan Klinis dari Pedoman',
      insightsUpper: 'WAWASAN KLINIS DARI PEDOMAN',
    },
    ehrChat: {
      title: 'Chat dengan EHR',
      ready: 'Asisten AI siap',
      unavailable: 'Tidak tersedia',
      initializing: 'Memulai...',
      close: 'Tutup chat',
      connecting: 'Menghubungkan ke EHR...',
      medicationHistory: 'Riwayat obat',
      medicationHistoryQuery: 'Tampilkan riwayat obat',
      recentLabs: 'Lab terbaru',
      recentLabsQuery: 'Ringkas hasil lab terbaru',
      previousVisits: 'Kunjungan sebelumnya',
      previousVisitsQuery: 'Tampilkan kunjungan sebelumnya',
      placeholder: 'Tanyakan data pasien, obat, hasil...',
      send: 'Kirim pesan',
    },
  },
};
